import { Flexbox, Modal, Text } from "@sede-enterprise/shell-ds-react-framework";
import { useContext, useEffect, useReducer, useState } from "react";
import {
  AMERICAS,
  ASIA,
  ASSET_SCOPE1_CI,
  AUSTRALIA,
  FOOT_NOTE,
  KPI_NCI_IMPACT,
  NATURAL_GAS,
  PIPELINE_GAS,
  POWER,
  RNG,
} from "../../api/constants";
import Step3Image from "../../images/Step3_empty.png";
import "../CalculatorForm/CalculatorForm.css";
import type { Dispatch } from "react";
import { FIRST_COL_DATA, FIRST_COL_DATA_SLMT_TS, one } from "../../constants";
import { useNetCarbonIntensity } from "../../context/NetCarbonIntensityContext";
import { AppContext } from "../../context/calculatorFormContext";
import { kpiContentReducer } from "../../reducers/ActionHandlers/KpiContentActionHandler";
import { kpiContentInitialState } from "../../reducers/InitialStates/KpiContentInitialState";
import type { IKpiContentData } from "../../types/types";
import { calculateImpactYearNCIDeals } from "../AllDeals/DealDetails/DealDetailFunctions";
import { KpiCardView } from "./KpiCardView";
import {
  createDealsFromInput,
  decorateCardTableData,
  decorateTableData,
  getNcidataForStepThreeTable,
} from "./KpiContentFunctions";
import { KpiMessageView } from "./KpiMessageView";
import { KpiTable } from "./KpiTable";
import { getCols } from "./utils";

// Helper function to compute region/commodity flags
const computeRegionCommodityFlags = (state: any) => ({
  isAmericasWithCommodityPg: state.commodity === PIPELINE_GAS && state.region === AMERICAS,
  isAmericasWithCommodityRng: state.commodity === RNG && state.region === AMERICAS,
  isAsiaWithCommodityPg: state.commodity === PIPELINE_GAS && state.region === ASIA,
  isAsiaWithCommodityRng: state.commodity === RNG && state.region === ASIA,
  isAustraliaWithCommodityPg: state.commodity === PIPELINE_GAS && state.region === AUSTRALIA,
  isAustraliaWithCommodityRng: state.commodity === RNG && state.region === AUSTRALIA,
  isCommodityPW: state.commodity === POWER,
  isCommodityPg: state.commodity === PIPELINE_GAS,
  isCommodityRng: state.commodity === RNG,
  isGasFiredPower:
    state.technologySource.includes(NATURAL_GAS) || state.technologySource.includes("LNG"),
});

const KPIContent = (props?: { kpiState?: IKpiContentData; kpiDispatch?: Dispatch<any> }) => {
  const { state, dispatch } = useContext(AppContext);
  const [kpiMessage, setKpiMessage] = useState<string[]>([]);
  // Allow CalculatorForm to pass down the KPI reducer so saving uses the same state
  const [localKpiState, localKpiDispatch] = useReducer(kpiContentReducer, kpiContentInitialState);
  const myState = props?.kpiState ?? localKpiState;
  const myDispatch = props?.kpiDispatch ?? localKpiDispatch;
  const { netCarbonIntensityData: nciData } = useNetCarbonIntensity();

  // Compute region/commodity flags
  const {
    isAmericasWithCommodityPg,
    isAmericasWithCommodityRng,
    isAsiaWithCommodityPg,
    isAsiaWithCommodityRng,
    isAustraliaWithCommodityPg,
    isAustraliaWithCommodityRng,
    isCommodityPW,
    isCommodityPg,
    isCommodityRng,
    isGasFiredPower,
  } = computeRegionCommodityFlags(state);

  // Helper function to normalize EVP name for comparison
  const normalizeEvpName = (evpName: string): string => {
    return evpName.toLowerCase().replace(/[^a-z0-9]/g, "");
  };

  // Check if EVP is T&S Products
  // T&S Products EVP requires additional KPIs: Volume-weighted Avg CI and Scope 3 Cat11 Emission
  // SLMT uses standard KPI calculations like other EVPs
  const normalizedEvp = normalizeEvpName(state.evp);
  const isTSProducts = normalizedEvp === "tsproducts";
  const isSLMT = normalizedEvp === "slmt";
  const shouldShowFootnote = !isTSProducts && !isSLMT;

  // Use extended KPI list for T&S Products only, standard list for other EVPs (including SLMT)
  let firstColData = [...(isTSProducts ? FIRST_COL_DATA_SLMT_TS : FIRST_COL_DATA)];

  // Adding new field at beginning of the firstColData array, if the selected technology source is 'Gas Fired Generation'
  if (isGasFiredPower) {
    firstColData = [ASSET_SCOPE1_CI, ...firstColData];
  }
  const parameters = {
    isAmericasWithCommodityPg,
    isAmericasWithCommodityRng,
    isAsiaWithCommodityPg,
    isAsiaWithCommodityRng,
    isAustraliaWithCommodityPg,
    isAustraliaWithCommodityRng,
    isCommodityPW,
    isCommodityPg,
    isCommodityRng,
    isGasFiredPower,
  };
  // set all the column headers
  const cols = getCols(state, state.stepTwoTableData);

  useEffect(() => {
    decorateTableData(state, dispatch, myState, myDispatch, setKpiMessage, {
      parameters,
      cols,
      firstColData,
    });
  }, [state.isGetOutputKpi]);

  useEffect(() => {
    decorateCardTableData(myState, myDispatch, dispatch, parameters, state);
  }, [myState.stepThreeTableData]);

  useEffect(() => {
    decorateCardTableData(myState, myDispatch, dispatch, parameters, state);
  }, [state.isGetOutputKpi]);

  const onClose = () => {
    myDispatch({
      type: "setIsCopiedToClipboard",
      payload: false,
    });
  };
  const netAbsEmissionYearWise = myState.netAbsEmsDataYearWise;
  const { stepTwoTableData, commodity, uomVal, operatingPlan } = state;
  const volumeYearWise = stepTwoTableData[stepTwoTableData.length - one];
  const getDeal = createDealsFromInput(netAbsEmissionYearWise, commodity, volumeYearWise, uomVal);
  const impactNciData = getDeal && calculateImpactYearNCIDeals(getDeal, nciData, operatingPlan);
  // Keep impactNciData in KPI state up to date for save logic
  useEffect(() => {
    if (
      impactNciData &&
      // Only dispatch if data is actually different (shallow compare by length and years)
      (!myState.impactNciData ||
        impactNciData.length !== myState.impactNciData.length ||
        impactNciData.some(
          (item, idx) =>
            !myState.impactNciData?.[idx] ||
            item.YEAR !== myState.impactNciData[idx].YEAR ||
            item.CHANGED_REPORTED_GROUP_NCI !==
              myState.impactNciData[idx].CHANGED_REPORTED_GROUP_NCI
        ))
    ) {
      myDispatch({ type: "setImpactNciData", payload: impactNciData });
    }
  }, [impactNciData, myState.impactNciData]);
  const stepThreeNciData =
    impactNciData &&
    getNcidataForStepThreeTable(
      getDeal,
      impactNciData,
      myState.stepThreeTableData,
      state.evp,
      state.stepTwoTableData,
      state
    );

  // Extract year-wise NCI Impact from stepThreeNciData and store in state
  useEffect(() => {
    if (stepThreeNciData) {
      const nciImpactRow = stepThreeNciData.find((row: any) => row.KPI === KPI_NCI_IMPACT);
      if (nciImpactRow) {
        // Remove non-year keys (KPI, Total, etc.)
        const yearWise: Record<number, string> = {};
        Object.keys(nciImpactRow).forEach((key) => {
          if (/^\d{4}$/.test(key)) {
            yearWise[Number(key)] = nciImpactRow[key as keyof typeof nciImpactRow] as string;
          }
        });
        // Only dispatch if yearWise is actually different from myState.nciImpactYearWise
        const prev = myState.nciImpactYearWise || {};
        const keys = Object.keys(yearWise);
        const prevKeys = Object.keys(prev);
        const isDifferent =
          keys.length !== prevKeys.length ||
          keys.some((k) => yearWise[Number(k)] !== prev[Number(k)]);
        if (isDifferent) {
          myDispatch({ type: "setNciImpactYearWise", payload: yearWise });
        }
      }
    }
  }, [stepThreeNciData]);

  // FIX: For T&S Products and SLMT, use the updated nciData (which includes our NCI Impact calculations)
  // instead of impactNciData for the KPI Summary card
  const isTSProductsOrSLMT = normalizedEvp === "tsproducts" || normalizedEvp === "slmt";

  let nciDataForSummaryCard = nciData;

  // Special handling for T&S Products/SLMT
  if (isTSProductsOrSLMT && impactNciData) {
    nciDataForSummaryCard = nciData.map((nciItem) => {
      const impactItem = impactNciData.find((impact) => impact.YEAR === nciItem.YEAR);

      // If nciData doesn't have CHANGED_REPORTED_GROUP_NCI but impactNciData does, use impactNciData
      if (
        !Object.prototype.hasOwnProperty.call(nciItem, "CHANGED_REPORTED_GROUP_NCI") &&
        impactItem &&
        Object.prototype.hasOwnProperty.call(impactItem, "CHANGED_REPORTED_GROUP_NCI")
      ) {
        return impactItem;
      }

      return nciItem;
    });
  }

  // Unified handling for all other EVPs (DR Power, Shell Energy, etc.)
  if (!isTSProductsOrSLMT && impactNciData) {
    nciDataForSummaryCard = nciData.map((nciItem) => {
      const impactItem = impactNciData.find((impact) => impact.YEAR === nciItem.YEAR);

      // If impactNciData has CHANGED_REPORTED_GROUP_NCI but nciData doesn't, use impactNciData
      if (
        impactItem &&
        Object.prototype.hasOwnProperty.call(impactItem, "CHANGED_REPORTED_GROUP_NCI") &&
        !Object.prototype.hasOwnProperty.call(nciItem, "CHANGED_REPORTED_GROUP_NCI")
      ) {
        return impactItem;
      }

      return nciItem;
    });
  }

  // Check if KPI data is ready to display
  const shouldShowKpiData =
    !state.isGetOutputKpiServiceFailure && state.isShowKpiData && state.isFormValidated;

  return shouldShowKpiData ? (
    <>
      <Modal
        open={myState.isCopiedToClipboard}
        onClose={onClose}
        showFooter={false}
        showHeader={false}
      >
        {(myState.copiedContent as string) + " is copied to clipboard."}
      </Modal>
      <Flexbox gap="20px">
        <KpiCardView
          cardTableData={myState.cardTableData}
          impactNciData={nciDataForSummaryCard}
          isTSProducts={isTSProducts}
          isSLMT={isSLMT}
        />
        <KpiMessageView kpiMessage={kpiMessage} isGasFiredPower={isGasFiredPower} />
      </Flexbox>
      <KpiTable cols={cols} stepThreeNciData={stepThreeNciData || []} isTSProducts={isTSProducts} />
      {/* Hide footnote for T&S Products and SLMT EVPs */}
      {shouldShowFootnote && (
        <Text size="small" style={{ color: "#00000" }}>
          <em>{FOOT_NOTE}</em>
        </Text>
      )}
    </>
  ) : (
    <>
      <Text type="span" className="kpi-label"></Text>
      <div className="empty-state-image">
        <img src={Step3Image} width="90" height="90" alt="KPI Table" />
      </div>
      <p className="empty-state-sub-label">Please complete Step 02 to generate Carbon KPIs</p>
    </>
  );
};
export default KPIContent;
